import React from "react";
import moment from "moment";

const CalendarActions = {
    SET_DATE: 'SET_DATE',
    SET_SELECTED_UNITS: 'SET_SELECTED_UNITS',
    SET_SEARCH_TEXT: 'SET_SEARCH_TEXT',
    SET_SELECTED_APPOINTMENT: 'SET_SELECTED_APPOINTMENT',
    SET_UNITS: 'SET_UNITS',
    SET_HIDE_DELETED_AND_CANCELLED: 'SET_HIDE_DELETED_AND_CANCELLED',
    REFRESH: 'REFRESH',
}

// date burada hesaplanmaz: modül yüklendiği anda sabitlenir ve UTC modundadır — hem gece
// yarısından sonra açık kalan sekmede takvim dünkü güne açılır, hem de TR'de 00:00–03:00
// arası UTC günü bir önceki gün olur. Bugün, provider her kurulduğunda yerel saatle alınır.
const defaultFilter = {
    date: null,
    selectedUnits: JSON.parse(localStorage.getItem('selected-units')) || [],
    text: '',
    hideDeletedAndCancelled: true
}

const defaultValue = {
    filter: defaultFilter,
    selectedAppointment: { appointmentId: null, patientId: null, unitId: null },
    units: [],
    changedState: 0,
    // Birim seçimi localStorage'a yazılsın mı. Takvim SAYFASI için evet (kullanıcının
    // sütun tercihi kalıcı). Gömülü/geçici kullanımlar (ör. randevu saati seçici) için
    // hayır: orada seçim tek bir birime sabitlenir ve kullanıcının takvim tercihini ezmemelidir.
    persistSelectedUnits: true
}

const CalendarContext = React.createContext(defaultValue);

function calendarReducer(state, action) {
    switch (action.type) {
        case CalendarActions.SET_DATE: {

            return {
                ...state,
                filter: {
                    ...state.filter,
                    date: action.data
                }
            }
        }
        case CalendarActions.SET_SELECTED_UNITS: {
            if (state.persistSelectedUnits !== false)
                localStorage.setItem('selected-units', JSON.stringify(action.data))

            return {
                ...state,
                filter: {
                    ...state.filter,
                    selectedUnits: action.data
                }
            }
        }
        case CalendarActions.SET_SELECTED_APPOINTMENT: {

            return {
                ...state,
                selectedAppointment: action.data
            }
        }
        case CalendarActions.SET_SEARCH_TEXT: {

            return {
                ...state,
                filter: {
                    ...state.filter,
                    text: action.data
                }
            }
        }
        case CalendarActions.SET_UNITS: {
            let selected
            if (state.persistSelectedUnits === false) {
                // Sabit seçim: dışarıdan verilen birim(ler) korunur, localStorage'a hiç bakılmaz.
                // Yine de yeni bir filter nesnesi dönüyoruz — Calendar ilk randevu çekimini
                // filter değişimine bağlı yapıyor (aşağıdaki REFRESH zinciri).
                selected = (state?.filter?.selectedUnits || []).filter(x => action?.data?.find(y => y.id === x))
            }
            else {
                selected = JSON.parse(localStorage.getItem('selected-units') || '[]')
                if (state?.filter?.selectedUnits?.length === 0) {
                    selected = [...action?.data?.map(x => x.id)]
                }
                selected = selected.filter(x => action?.data?.find(y => y.id === x))
                localStorage.setItem('selected-units', JSON.stringify(selected))
            }

            return {
                ...state,
                units: action.data,
                filter: {
                    ...state.filter,
                    selectedUnits: selected
                }
            }
        }
        case CalendarActions.SET_HIDE_DELETED_AND_CANCELLED: {
            return {
                ...state,
                filter: {
                    ...state.filter,
                    hideDeletedAndCancelled: action.data
                }
            }
        }
        case CalendarActions.REFRESH: {
            return {
                ...state,
                changedState: state.changedState + 1
            }
        }
        default: {
            return state;
        }
    }
}

/**
 * initialDate / initialSelectedUnits / persistSelectedUnits: gömülü kullanımlar içindir
 * (randevu saati seçici gibi). Hiçbiri verilmezse davranış eskisiyle birebir aynıdır —
 * takvim sayfası bugüne ve localStorage'daki birim seçimine açılır.
 */
function CalendarProvider({ children, initialDate, initialSelectedUnits, persistSelectedUnits = true }) {
    const [calendar, dispatch] = React.useReducer(calendarReducer, {
        ...defaultValue,
        persistSelectedUnits,
        filter: {
            ...defaultFilter,
            date: initialDate ? moment(initialDate) : moment(),
            selectedUnits: initialSelectedUnits ?? defaultFilter.selectedUnits,
        }
    });

    const value = { calendar, dispatch };

    return (
        <CalendarContext.Provider value={value}>
            {children}
        </CalendarContext.Provider>
    )
}

function useCalendar() {
    const context = React.useContext(CalendarContext);
    if (!context)
        throw new Error('Calendar Context Error!');

    const { calendar, dispatch } = context;

    const setDate = (date) => {
        dispatch({
            type: CalendarActions.SET_DATE,
            data: date
        })
    }

    const setSelectedUnits = (unitIdList) => {
        dispatch({
            type: CalendarActions.SET_SELECTED_UNITS,
            data: unitIdList
        })
    }

    const setSearchText = (text) => {
        dispatch({
            type: CalendarActions.SET_SEARCH_TEXT,
            data: text
        })
    }

    const setSelectedAppointment = ({ appointmentId, patientId, unitId, checkIn, stateId }) => {
        dispatch({
            type: CalendarActions.SET_SELECTED_APPOINTMENT,
            data: { appointmentId, patientId, unitId, checkIn, stateId }
        })
    }

    const setUnits = (units) => {
        dispatch({
            type: CalendarActions.SET_UNITS,
            data: units
        })
    }

    const refresh = () => {
        dispatch({
            type: CalendarActions.REFRESH,
        })
    }

    const setHideDeletedAndCancelled = (value) => {
        dispatch({
            type: CalendarActions.SET_HIDE_DELETED_AND_CANCELLED,
            data: value
        })
    }

    return {
        filter: calendar.filter,
        selectedAppointment: calendar.selectedAppointment,
        units: calendar.units,
        changedState: calendar.changedState,
        setSelectedAppointment,
        setDate,
        setSearchText,
        setSelectedUnits,
        setUnits,
        setHideDeletedAndCancelled,
        refresh
    }
}

export { CalendarProvider, useCalendar }